import { Link } from '@tanstack/react-router';
import { CheckCircle2, XCircle, AlertCircle, Clock } from 'lucide-react';

/**
 * Where SSLCommerz sends the browser after checkout. The payment is credited
 * server-side (IPN / success callback → Validation API), so this page is purely
 * informational and works even if the session was dropped on the external round
 * trip. Status comes from the redirect query (?status=success|failed|cancelled|pending).
 */
const STATES: Record<string, { icon: typeof CheckCircle2; tone: string; title: string; body: string }> = {
  success: {
    icon: CheckCircle2,
    tone: 'text-emerald-600 dark:text-emerald-400',
    title: 'Payment received',
    body: 'Your payment was confirmed and your subscription is now active. A receipt is on its way to your email.',
  },
  pending: {
    icon: Clock,
    tone: 'text-amber-600 dark:text-amber-400',
    title: 'Payment is processing',
    body: 'We received your payment and are confirming it with the gateway. Your subscription updates automatically once confirmed — no need to pay again.',
  },
  failed: {
    icon: XCircle,
    tone: 'text-rose-600 dark:text-rose-400',
    title: 'Payment failed',
    body: 'The payment did not go through and you have not been charged. You can try again from your subscription page.',
  },
  cancelled: {
    icon: AlertCircle,
    tone: 'text-zinc-500 dark:text-zinc-400',
    title: 'Payment cancelled',
    body: 'You cancelled the payment before it completed. Nothing was charged.',
  },
};

export function PaymentReturn() {
  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const status = params.get('status') ?? 'pending';
  const state = STATES[status] ?? STATES.pending!;
  const Icon = state.icon;

  return (
    <div className="min-h-screen grid place-items-center bg-zinc-50 dark:bg-zinc-950 px-4">
      <div className="w-full max-w-md text-center">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 p-8 sm:p-10 shadow-sm">
          <Icon className={`w-12 h-12 mx-auto ${state.tone}`} strokeWidth={1.5} />
          <h1 className="mt-4 text-xl font-semibold text-zinc-950 dark:text-zinc-50">{state.title}</h1>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">{state.body}</p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/subscription"
              className="w-full sm:w-auto px-4 py-2.5 rounded-lg bg-brand text-white dark:bg-brand-dm dark:text-zinc-950 text-sm font-medium hover:opacity-90 transition"
            >
              Go to subscription
            </Link>
            <Link
              to="/"
              className="w-full sm:w-auto px-4 py-2.5 rounded-lg border border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-200 text-sm font-medium hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
            >
              Dashboard
            </Link>
          </div>
          <p className="mt-5 text-xs text-zinc-400 dark:text-zinc-500">
            Signed out? Your payment is safe — just sign in again to see the update.
          </p>
        </div>
      </div>
    </div>
  );
}
